/**
 * menuCategories.js — 사이드바·홈 카드 분류를 DB 에서 (2026-09-15, 사용자 확정 a안)
 *
 * 전에는 분류가 menuConfig.js 의 그룹 배치에 박혀 있어, 메뉴를 다른 그룹으로 옮기려면 코드를 고쳐야 했다.
 * 이제 saas_modules.category(DB)가 정본이다 — 메뉴의 권한코드 → 모듈 → 분류. DB 관리 화면에서 고치면 옮겨진다.
 *
 * 규칙
 *  - 분류는 "어디 놓이나"만 정한다. "보이나"는 여전히 권한(filterMenuGroups)이 정한다.
 *  - DB 를 못 읽었거나 매핑이 없는 메뉴는 **코드 분류(menuConfig)** 그대로. 분류가 틀려도 누출은 없다.
 *  - '현황'·'시스템' 은 화면이 고정으로 다룬다(상단 바로가기 / 회사를 바꿔도 늘 보이는 운영자 도구).
 *    그래서 ① 이 두 그룹의 메뉴는 DB 분류를 따르지 않고 ② 다른 메뉴를 DB 에서 이 두 이름으로 옮기는 것도 무시한다.
 *  - 그룹 순서는 DB(app_settings.sidebar_category_order) → 코드 순서 → 새 분류. '시스템' 은 늘 맨 끝.
 *  - 한 그룹 안의 순서는 화면이 가나다로 정렬하므로 여기서 정하지 않는다.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

export const PINNED_GROUPS = new Set(['현황', '시스템']);
const LAST_GROUP = '시스템';

// 요청은 한 번만 — 사이드바(데스크톱·모바일)와 홈 카드가 같이 쓴다. 로그인 토큰이 바뀌면 다시 받는다.
let cached = null;
let cachedFor = null;
function currentTokenKey() {
  try { return sessionStorage.getItem('pg_imp_token') || localStorage.getItem('accessToken') || ''; } catch (_) { return ''; }
}

export function loadMenuCategories() {
  const tokenKey = currentTokenKey();
  if (cached && cachedFor === tokenKey) return cached;
  cachedFor = tokenKey;
  cached = apiClient.get('/api/my/menu-categories')
    .then((r) => (r.ok && r.data && typeof r.data.byPerm === 'object'
      ? { byPerm: r.data.byPerm || {}, order: Array.isArray(r.data.order) ? r.data.order : [] }
      : null))
    .catch(() => null)
    .then((v) => { if (!v) cached = null; return v; }); // 실패는 기억하지 않는다 — 다음 화면에서 다시 시도
  return cached;
}

/** DB 분류. 받기 전·실패 시 null(= 코드 분류). */
export function useMenuCategories() {
  const [cats, setCats] = useState(null);
  useEffect(() => {
    let on = true;
    loadMenuCategories().then((v) => { if (on) setCats(v); });
    return () => { on = false; };
  }, []);
  return cats;
}

function itemCategory(item, codeGroup, byPerm) {
  if (PINNED_GROUPS.has(codeGroup)) return codeGroup;
  const codes = item.permCode ? [item.permCode] : (Array.isArray(item.permCodeAny) ? item.permCodeAny : []);
  for (const code of codes) {
    const cat = byPerm[code];
    if (cat && !PINNED_GROUPS.has(cat)) return cat;
  }
  return codeGroup;
}

/** 그룹 이름 정렬: DB 순서 → 코드 순서 → 새 분류(가나다). '시스템' 은 늘 끝. */
export function orderGroupNames(names, dbOrder = [], codeOrder = []) {
  const rank = (n) => {
    if (n === LAST_GROUP) return [3, 0, n];
    const i = dbOrder.indexOf(n);
    if (i >= 0) return [0, i, n];
    const j = codeOrder.indexOf(n);
    if (j >= 0) return [1, j, n];
    return [2, 0, n];
  };
  return [...names].sort((a, b) => {
    const ra = rank(a); const rb = rank(b);
    return ra[0] - rb[0] || ra[1] - rb[1] || ra[2].localeCompare(rb[2], 'ko');
  });
}

/** MENU_GROUPS 를 DB 분류로 다시 묶는다. cats 가 없으면 원본 그대로 돌려준다. */
export function regroupMenuGroups(menuGroups, cats) {
  if (!cats) return menuGroups;
  const byName = new Map();
  for (const g of menuGroups) {
    for (const it of g.items) {
      const cat = itemCategory(it, g.group, cats.byPerm || {});
      if (!byName.has(cat)) byName.set(cat, []);
      byName.get(cat).push(it);
    }
  }
  return orderGroupNames([...byName.keys()], cats.order || [], menuGroups.map((g) => g.group))
    .map((name) => ({ group: name, items: byName.get(name) }));
}

/** 본사 주소 → 그 메뉴가 놓인 분류(회사별 메뉴 세트가 같은 기능의 일반 메뉴를 따라가게). 없으면 null. */
export function categoryOfPath(groups, path) {
  if (!path) return null;
  for (const g of groups) if (g.items.some((it) => it.to === path)) return g.group;
  return null;
}
